import { createSelector } from '@reduxjs/toolkit';
import { chain, orderBy } from 'lodash';

import { InsightsState } from 'app-redux/InsightsState';
import * as dto from 'client/dto';
import { DefaultLocationInfoSortFns, DefaultLocationInfoSortOrders, VaccineSortOptionsFlat } from 'models/SortByOptions';
import { Today } from 'utils/dateTimeHelpers';
import { containsNoCaseNoLocale } from 'utils/stringHelpers';

import { computeMaxYValueOfDailyNewCases, mapToVaccineTrackerCellData, trimNullBeginningOfArray } from './covidDataSuite.helpers';
import { StartDateCell, VaccineLocationRow } from './covidDataSuite.models';

// covidDataSuiteSelectors
const lineSeriesSelector = (state: InsightsState) => state?.covidDataSuite?.lineSeriesByKeyAndLocationId;
const graphColorAvailabilitySelector = (state: InsightsState) => state?.covidDataSuite?.graphColorAvailability;

export const dataTableLastUpdatedSelector = (state: InsightsState) => state.covidDataSuite?.dataTableLastUpdated;
export const locationInfoByIdSelector = (state: InsightsState) => state?.covidDataSuite?.locationInfoById;
export const getEpiCurveError = (state: InsightsState) => state.covidDataSuite?.epiCurveError;
export const getIsEpiCurvesLoading = (state: InsightsState) => state.covidDataSuite?.isEpiCurvesLoading;
export const isDownloadingCovidData = (state: InsightsState) => state.covidDataSuite?.isDownloadingCovidData;
export const hasCovidDataDownloadError = (state: InsightsState) => state.covidDataSuite?.hasCovidDataDownloadError;
export const numOfLoadingVaccineLocations = (state: InsightsState) => state.covidDataSuite?.numOfLoadingVaccineLocations;
export const getVaccineTrackerLocationIds = (state: InsightsState) => state?.covidDataSuite?.vaccineLocationIds;
export const getVaccineLocationInfoById = (state: InsightsState) => state?.covidDataSuite?.vaccineLocationInfoById;
export const getVaccineRateModelByLocationId = (state: InsightsState) => state?.covidDataSuite?.vaccineRateModelByLocationId;
export const getActiveVaccineSort = (state: InsightsState) => state?.covidDataSuite?.activeVaccineSort;
export const getVaccineFilterString = (state: InsightsState) => state?.covidDataSuite?.vaccineFilterString;

export const getReportedCasesLineSeries = createSelector(lineSeriesSelector, lineSeries => lineSeries?.reported);
export const getDeathsLineSeries = createSelector(lineSeriesSelector, lineSeries => lineSeries?.deaths);
export const getWeeklyAvgNewCasesLineSeries = createSelector(lineSeriesSelector, lineSeries => lineSeries?.weeklyAvgNewCases);
export const getWeeklyAvgNewCasesPer100KLineSeries = createSelector(lineSeriesSelector, lineSeries => lineSeries?.weeklyAvgNewCasesPer100K);
export const getCasesPer7DaysLineSeries = createSelector(lineSeriesSelector, lineSeries => lineSeries?.casesPer7Days);
export const getCasesPer7DaysPer100KLineSeries = createSelector(lineSeriesSelector, lineSeries => lineSeries?.casesPer7DaysPer100K);
export const getCasesPer14DaysLineSeries = createSelector(lineSeriesSelector, lineSeries => lineSeries?.casesPer14Days);
export const getCasesPer14DaysPer100KLineSeries = createSelector(lineSeriesSelector, lineSeries => lineSeries?.casesPer14DaysPer100K);
export const getTotalTestsOwidLineSeries = createSelector(lineSeriesSelector, lineSeries => lineSeries?.totalTestsOwid);
export const getTestsPer14DaysLineSeries = createSelector(lineSeriesSelector, lineSeries => lineSeries?.testsPer14Days);
export const getTestsPer14DaysPer100KLineSeries = createSelector(lineSeriesSelector, lineSeries => lineSeries?.testsPer14DaysPer100K);

export const getNumOfLocations = createSelector(locationInfoByIdSelector, info => Object.keys(info ?? {}).length);
export const noAddedLocations = createSelector(getNumOfLocations, num => num === 0);
export const hasNationalLocations = createSelector(
  locationInfoByIdSelector,
  info => Object.values(info ?? {}).filter(loc => loc.locationType === dto.LocationType.Country)?.length > 0
);
export const getLocationIds = createSelector(locationInfoByIdSelector, info => Object.keys(info ?? {}).map(x => +x));
export const getVaccineTrackerLocationInfos = createSelector(
  getVaccineTrackerLocationIds,
  getVaccineLocationInfoById,
  (vaccineLocationIds, vaccineLocationInfoById) =>
    orderBy(
      vaccineLocationIds.map(x => vaccineLocationInfoById[x]),
      DefaultLocationInfoSortFns,
      DefaultLocationInfoSortOrders
    )
);
export const getVaccineTrackerLocationsTotalCount = createSelector(
  getVaccineTrackerLocationIds,
  vaccineLocationIds => vaccineLocationIds?.length ?? 0
);

export const getSelectedLocationIdsOrdered = createSelector(graphColorAvailabilitySelector, colors =>
  Object.values(colors ?? {}).filter(val => val != null)
);

export const getReportedCasesLineSeriesOrdered = createSelector(
  [getSelectedLocationIdsOrdered, getReportedCasesLineSeries],
  (orderedIds, lineSeries) => orderedIds.filter(id => lineSeries[id] != null).map(id => trimNullBeginningOfArray(lineSeries[id]))
);
export const getDeathsLineSeriesOrdered = createSelector([getSelectedLocationIdsOrdered, getDeathsLineSeries], (orderedIds, lineSeries) =>
  orderedIds.filter(id => lineSeries[id] != null).map(id => trimNullBeginningOfArray(lineSeries[id]))
);
export const getWeeklyAvgNewCasesLineSeriesOrdered = createSelector(
  [getSelectedLocationIdsOrdered, getWeeklyAvgNewCasesLineSeries],
  (orderedIds, lineSeries) => orderedIds.filter(id => lineSeries[id] != null).map(id => trimNullBeginningOfArray(lineSeries[id]))
);
export const getWeeklyAvgNewCasesPer100KLineSeriesOrdered = createSelector(
  [getSelectedLocationIdsOrdered, getWeeklyAvgNewCasesPer100KLineSeries],
  (orderedIds, lineSeries) => orderedIds.filter(id => lineSeries[id] != null).map(id => trimNullBeginningOfArray(lineSeries[id]))
);
export const getCasesPer7DaysLineSeriesOrdered = createSelector(
  [getSelectedLocationIdsOrdered, getCasesPer7DaysLineSeries], // TODO: PT-4345
  (orderedIds, lineSeries) => orderedIds.filter(id => lineSeries[id] != null).map(id => trimNullBeginningOfArray(lineSeries[id]))
);
export const getCasesPer7DaysPer100KLineSeriesOrdered = createSelector(
  [getSelectedLocationIdsOrdered, getCasesPer7DaysPer100KLineSeries], // TODO: PT-4345
  (orderedIds, lineSeries) => orderedIds.filter(id => lineSeries[id] != null).map(id => trimNullBeginningOfArray(lineSeries[id]))
);
export const getCasesPer14DaysLineSeriesOrdered = createSelector(
  [getSelectedLocationIdsOrdered, getCasesPer14DaysLineSeries],
  (orderedIds, lineSeries) => orderedIds.filter(id => lineSeries[id] != null).map(id => trimNullBeginningOfArray(lineSeries[id]))
);
export const getCasesPer14DaysPer100KLineSeriesOrdered = createSelector(
  [getSelectedLocationIdsOrdered, getCasesPer14DaysPer100KLineSeries],
  (orderedIds, lineSeries) => orderedIds.filter(id => lineSeries[id] != null).map(id => trimNullBeginningOfArray(lineSeries[id]))
);
export const getTotalTestsOwidLineSeriesOrdered = createSelector(
  [getSelectedLocationIdsOrdered, getTotalTestsOwidLineSeries],
  (orderedIds, lineSeries) => orderedIds.filter(id => lineSeries[id] != null).map(id => trimNullBeginningOfArray(lineSeries[id]))
);
export const getTestsPer14DaysLineSeriesOrdered = createSelector(
  [getSelectedLocationIdsOrdered, getTestsPer14DaysLineSeries],
  (orderedIds, lineSeries) => orderedIds.filter(id => lineSeries[id] != null).map(id => trimNullBeginningOfArray(lineSeries[id]))
);
export const getTestsPer14DaysPer100KLineSeriesOrdered = createSelector(
  [getSelectedLocationIdsOrdered, getTestsPer14DaysPer100KLineSeries],
  (orderedIds, lineSeries) => orderedIds.filter(id => lineSeries[id] != null).map(id => trimNullBeginningOfArray(lineSeries[id]))
);

export const getVaccineTrackerTableRows = createSelector(
  getVaccineTrackerLocationIds,
  getVaccineLocationInfoById,
  getVaccineRateModelByLocationId,
  getActiveVaccineSort,
  getVaccineFilterString,
  (orderedIds, locationInfoById, vaccineRateModelById, activeSort, filterString) => {
    const vaccineInfoArr = orderedIds
      .map(
        id =>
          ({
            id: id,
            name: locationInfoById[id]?.name,
            displayName: locationInfoById[id]?.displayName,
            countryName: locationInfoById[id]?.countryName,
            provinceName: locationInfoById[id]?.provinceName,
            locationType: locationInfoById[id]?.locationType,
            stringency: mapToVaccineTrackerCellData(vaccineRateModelById[id].totalAdult, 'stringency'),
            totalAdult: {
              ...mapToVaccineTrackerCellData(vaccineRateModelById[id].totalAdult, 'total-pop'),
              secondaryData: mapToVaccineTrackerCellData(vaccineRateModelById[id].highRisk, 'high-risk'),
            },
            caseLevels: mapToVaccineTrackerCellData(vaccineRateModelById[id].totalAdult, 'case-level'),
            casesMaxYValue: computeMaxYValueOfDailyNewCases(vaccineRateModelById[id].totalAdult),
          } as VaccineLocationRow)
      )
      .filter(
        x =>
          containsNoCaseNoLocale(x.locationType === dto.LocationType.Country ? x.displayName : x.name, filterString) ||
          containsNoCaseNoLocale(x.countryName, filterString) ||
          containsNoCaseNoLocale(x.provinceName, filterString)
      );

    const sortOption = VaccineSortOptionsFlat.find(opt => opt.value === activeSort);

    return sortOption ? orderBy(vaccineInfoArr, sortOption.keys, sortOption.orders) : vaccineInfoArr;
  }
);
export const isVaccineLocationsEmpty = createSelector(getVaccineTrackerLocationIds, ids => !ids?.length);

export const getMaxCasesAcrossAllLocations = createSelector(getVaccineRateModelByLocationId, vaccineRateModelById => {
  if (vaccineRateModelById == null) return 10;

  const maxOfAll = chain(vaccineRateModelById)
    .values()
    .map(vaccineRateModel => vaccineRateModel.totalAdult?.historicalVaccinationData)
    .flatten()
    .filter(vaccinationData => vaccinationData?.date >= StartDateCell && vaccinationData?.date <= Today)
    .map(vaccinationData => vaccinationData?.dailyNewCasesPer100000 ?? 0)
    .max()
    .value();

  return !maxOfAll ? 10 : Math.ceil(maxOfAll / 10) * 10;
});
